/**
 * Sentry 에러 모니터링 설정
 * FE #13: Sentry 로깅 통합
 *
 * 환경 변수:
 * - VITE_SENTRY_DSN: Sentry 프로젝트 DSN
 * - VITE_SENTRY_ENVIRONMENT: 환경 (development, staging, production)
 */

import * as Sentry from '@sentry/react';
import { logger } from '@/utils/logger';
import { scrubEvent, scrubTransactionEvent } from '@/lib/sentry/piiScrubber';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE;

// 디버깅: 환경 변수 로딩 확인 (개발 환경에서만)
if (SENTRY_ENVIRONMENT === 'development') {
  logger.debug('[Sentry Debug] 환경 변수:', {
    DSN: SENTRY_DSN ? `설정됨 (${SENTRY_DSN.substring(0, 30)}...)` : '미설정',
    ENVIRONMENT: SENTRY_ENVIRONMENT,
    RELEASE: SENTRY_RELEASE || '미설정',
  });
}

/**
 * Sentry 초기화
 * DSN이 설정되지 않은 경우 초기화하지 않음
 */
export function initSentry(): void {
  if (!SENTRY_DSN) {
    logger.warn('[Sentry] DSN이 설정되지 않아 초기화하지 않습니다.');
    return;
  }

  Sentry.init(createSentryOptions());

  console.info(`[Sentry] 초기화 완료 (환경: ${SENTRY_ENVIRONMENT})`);
}

export function createSentryOptions(): Parameters<typeof Sentry.init>[0] {
  return {
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release: SENTRY_RELEASE,

    // 에러 샘플링 비율 (1.0 = 100%)
    sampleRate: 1.0,

    // 성능 모니터링 트레이스 샘플링 비율
    tracesSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.1 : 1.0,

    // 리플레이 샘플링 비율 (에러 발생 시 세션 리플레이)
    replaysOnErrorSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.5 : 1.0,

    // 일반 세션 리플레이 샘플링 비율
    replaysSessionSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.01 : 0,

    // 통합 기능
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // 개인정보 보호를 위해 텍스트/이미지 마스킹
        // 프로덕션에서는 개인정보 보호를 위해 마스킹 활성화
        maskAllText: SENTRY_ENVIRONMENT === 'production',
        blockAllMedia: SENTRY_ENVIRONMENT === 'production',
        // 민감한 입력 필드 마스킹 (비밀번호, 카드번호 등)
        maskAllInputs: true,
      }),
    ],

    // 개발 환경에서는 콘솔 출력
    debug: SENTRY_ENVIRONMENT === 'development',

    // 무시할 에러 패턴
    ignoreErrors: [
      // 네트워크 에러 (일시적)
      'Network Error',
      // Keep explicit operation reports such as notification read failures.
      /^Failed to fetch$/,
      // 취소된 요청
      'AbortError',
      // 브라우저 확장 프로그램 에러
      /chrome-extension/,
      /moz-extension/,
      // React 개발 모드 에러
      'Loading chunk',
    ],

    // 이벤트 전송 전 처리
    // - PII Scrubber (#351, admin#1304 정합 — LFPDPPP 컴플라이언스)
    //   8 영역 / 5 패턴 / 21+ 키 마스킹 (request / extra / tags / user / breadcrumbs / message / contexts / exception)
    // - 개발 환경 콘솔 디버그 (logger.error 무한 재귀 회피 — console 직접 호출)
    beforeSend(event, hint) {
      if (SENTRY_ENVIRONMENT === 'development') {
        console.error('[Sentry Debug]', hint.originalException || hint.syntheticException);
      }

      return scrubEvent(event);
    },
    beforeSendTransaction(event) {
      return scrubTransactionEvent(event);
    },
  };
}

/**
 * 수동 에러 캡처
 */
export function captureError(error: Error, context?: Record<string, unknown>): void {
  if (!SENTRY_DSN) {
    logger.error('[Error]', error, context);
    return;
  }

  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * 사용자 정보 설정
 */
export function setUser(user: { id: string; email?: string; name?: string } | null): void {
  Sentry.setUser(user);
}

/**
 * 사용자 정의 태그 설정
 */
export function setTag(key: string, value: string): void {
  Sentry.setTag(key, value);
}

/**
 * 브레드크럼 추가 (사용자 행동 추적)
 */
export function addBreadcrumb(
  message: string,
  category: string,
  level: 'info' | 'warning' | 'error' = 'info'
): void {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    timestamp: Date.now() / 1000,
  });
}

// Sentry ErrorBoundary 컴포넌트 re-export
export const SentryErrorBoundary = Sentry.ErrorBoundary;
export const withProfiler = Sentry.withProfiler;
